/**
 * recipeBookStore — pending edits, staged deletes, conflicts and validation (spec §12.3).
 *
 * Pure functions plus one hook. Nothing here talks to the network; the container owns
 * that. Keeping the rules pure is what lets them be unit-tested without a DOM, and the
 * validation below deliberately mirrors the SERVER's rules (§10.4) for instant feedback
 * — the server remains the authority and re-checks everything.
 *
 * The three maps and the rules that govern them are the spec's, verbatim:
 *   • setting a value back to its original REMOVES it from pendingChanges
 *   • Discard clears everything (after a prompt, which the container owns)
 *   • a successful save clears all three and applies the new row_versions
 *   • edit-then-delete on one row: the delete wins, the edit is dropped (§16 case 12)
 */

import { useCallback, useMemo, useState } from 'react';

/**
 * DISPLAY CONFIG — units.
 *
 * The product owner's requirement is that the Recipe Book shows QUANTITY VALUES ONLY:
 * the number is what is displayed and what is typed, and no unit symbol appears beside
 * it. Units are still carried in the API payload — the server needs them to pick the
 * decimal-place rule (kg/L → 4 dp, g/ml/pcs → 2 dp) and to write the audit line — they
 * are simply not rendered.
 *
 * One constant, so the decision is reversible in a single edit rather than by hunting
 * through JSX. Set to true to restore the printed book's separate UNIT column.
 */
export const SHOW_UNIT_COLUMN = false;

/** rowKey = scope + ids. Must match the server's identity for a row. */
export function rowKeyOf(row) {
  return [
    row.scope,
    row.menu_item_id ?? '',
    row.comp_id ?? '',
    row.prep_id ?? '',
    row.item_id ?? '',
  ].join(':');
}

/** Only the identifying fields travel to the server; never the whole render row. */
export function identityOf(row) {
  const id = { scope: row.scope };
  if (row.menu_item_id !== undefined && row.menu_item_id !== null) id.menu_item_id = row.menu_item_id;
  if (row.comp_id !== undefined && row.comp_id !== null) id.comp_id = row.comp_id;
  if (row.prep_id !== undefined && row.prep_id !== null) id.prep_id = row.prep_id;
  if (row.item_id !== undefined && row.item_id !== null) id.item_id = row.item_id;
  return id;
}

/** The value a row currently holds, whichever field its scope calls it. */
export function currentValueOf(row) {
  if (row.per_plate !== undefined) return Number(row.per_plate);
  if (row.batch_quantity !== undefined) return Number(row.batch_quantity);
  return Number(row.quantity);
}

const INTEGER_SCOPES = new Set(['comp_in_dish', 'comp_rate']);

const decimalsOf = (value) => {
  const s = String(value);
  if (!s.includes('.')) return 0;
  return s.split('.')[1].replace(/0+$/, '').length;
};

/** §10.4: kg / L allow 4 dp; everything else 2. Mirrors the server exactly. */
export const maxDecimalsForUnit = (unit) => {
  const u = String(unit || '').trim().toLowerCase();
  return u === 'kg' || u === 'l' ? 4 : 2;
};

export const MAX_VALUE = 1000000;

/**
 * Validate a typed value against its row. Returns null when valid, else a message.
 * Deliberately the same wording the server returns, so a client-caught problem and a
 * server-caught one do not read as two different bugs.
 */
export function validateValue(row, raw) {
  const text = String(raw ?? '').trim();
  if (text === '') return 'Enter a quantity';

  const value = Number(text);
  if (!Number.isFinite(value)) return 'Quantity must be a number';
  if (value <= 0) return 'Quantity must be greater than zero';
  if (value > MAX_VALUE) return `Quantity must be ${MAX_VALUE.toLocaleString('en-IN')} or less`;

  if (INTEGER_SCOPES.has(row.scope)) {
    if (!Number.isInteger(value)) return 'Per-plate must be a whole number';
    if (value < 1) return 'Must be at least 1';
    if (value > 100) return 'Must be 100 or less';
    return null;
  }

  const allowed = row.scope === 'prep_ingredient'
    ? Math.min(maxDecimalsForUnit(row.unit_symbol), 3) // NUMERIC(10,3)
    : maxDecimalsForUnit(row.unit_symbol);
  if (decimalsOf(value) > allowed) {
    return `At most ${allowed} decimal place${allowed === 1 ? '' : 's'} here`;
  }

  return null;
}

/** Quantity display. Integers stay integers; everything else loses trailing noise. */
export function formatQuantity(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return String(n);
  return String(Number(n.toFixed(4)));
}

// ── The hook ────────────────────────────────────────────────────────────────

export function useRecipeBookStore() {
  const [pendingChanges, setPendingChanges] = useState(() => new Map());
  const [pendingDeletes, setPendingDeletes] = useState(() => new Map());
  const [conflicts, setConflicts] = useState(() => new Map());

  /** Type a new value into a row. */
  const setValue = useCallback((row, rawValue) => {
    const key = rowKeyOf(row);
    const original = currentValueOf(row);

    setPendingChanges((prev) => {
      const next = new Map(prev);
      const text = String(rawValue ?? '').trim();

      // Back to what it was ⇒ no longer a pending change (§12.3).
      if (text !== '' && Number(text) === original) {
        next.delete(key);
        return next;
      }

      const error = validateValue(row, rawValue);
      next.set(key, {
        scope: row.scope,
        ids: identityOf(row),
        name: row.display_name || row.name,
        unit: row.unit_symbol,
        oldValue: original,
        newValue: text === '' ? '' : Number(text),
        raw: rawValue,
        rowVersion: row.row_version,
        valid: !error,
        error,
      });
      return next;
    });

    // Typing over a conflicted row is the user choosing "keep mine"; the blue
    // state stops being useful the moment they act on it.
    setConflicts((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  /** Stage / unstage a delete. §16 case 12: a delete wins over a pending edit. */
  const toggleDelete = useCallback((row, meta = {}) => {
    const key = rowKeyOf(row);
    setPendingDeletes((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, {
          scope: row.scope,
          ids: identityOf(row),
          name: row.display_name || row.name,
          unit: row.unit_symbol,
          value: currentValueOf(row),
          rowVersion: row.row_version,
          warning: meta.warning || null,
        });
      }
      return next;
    });
    setPendingChanges((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const discardAll = useCallback(() => {
    setPendingChanges(new Map());
    setPendingDeletes(new Map());
    setConflicts(new Map());
  }, []);

  /** After a successful save: everything clears (§12.3). */
  const clearAfterSave = useCallback(() => {
    setPendingChanges(new Map());
    setPendingDeletes(new Map());
    setConflicts(new Map());
  }, []);

  /** A 409 marks rows blue and KEEPS every edit (§12.8: never clear the user's edits). */
  const applyConflicts = useCallback((serverConflicts) => {
    setConflicts(() => {
      const next = new Map();
      (serverConflicts || []).forEach((c) => {
        next.set(rowKeyOf(c), {
          theirValue: c.current_value,
          theirVersion: c.current_version,
          yourVersion: c.your_version,
          name: c.name,
        });
      });
      return next;
    });
  }, []);

  /**
   * "Keep mine" — hold on to my value but adopt THEIR row_version.
   *
   * Keeping the stale version would 409 forever: the point of the choice is that the
   * user has now SEEN what changed underneath them, which is exactly the condition
   * RULE 5 protects ("never overwrite a value the user has not seen"). Adopting the
   * new version records that they saw it.
   */
  const keepMine = useCallback((key, theirVersion) => {
    setPendingChanges((prev) => {
      const existing = prev.get(key);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(key, { ...existing, rowVersion: theirVersion });
      return next;
    });
    setPendingDeletes((prev) => {
      const existing = prev.get(key);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(key, { ...existing, rowVersion: theirVersion });
      return next;
    });
    setConflicts((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  /** "Take theirs" — drop my edit for that row and clear its conflict. */
  const takeTheirs = useCallback((key) => {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    setPendingDeletes((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    setConflicts((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  /** A 422 marks the named rows invalid without touching anything else. */
  const applyServerFailures = useCallback((failures) => {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      (failures || []).forEach((f) => {
        const key = rowKeyOf(f);
        const existing = next.get(key);
        if (existing) next.set(key, { ...existing, valid: false, error: f.reason });
      });
      return next;
    });
  }, []);

  const stats = useMemo(() => {
    const edits = [...pendingChanges.values()];
    return {
      editCount: edits.length,
      deleteCount: pendingDeletes.size,
      invalidCount: edits.filter((e) => !e.valid).length,
      conflictCount: conflicts.size,
      dirty: edits.length > 0 || pendingDeletes.size > 0,
    };
  }, [pendingChanges, pendingDeletes, conflicts]);

  /** The exact payload PATCH /structured/batch expects (§9.3). */
  const buildPayload = useCallback(() => ({
    updates: [...pendingChanges.values()]
      .filter((c) => c.valid)
      .map((c) => ({ ...c.ids, new_value: c.newValue, row_version: c.rowVersion })),
    deletes: [...pendingDeletes.values()].map((d) => ({ ...d.ids, row_version: d.rowVersion })),
  }), [pendingChanges, pendingDeletes]);

  return {
    pendingChanges,
    pendingDeletes,
    conflicts,
    stats,
    setValue,
    toggleDelete,
    discardAll,
    clearAfterSave,
    applyConflicts,
    applyServerFailures,
    keepMine,
    takeTheirs,
    buildPayload,
  };
}
